import { useMemo, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { Button, Paper, Stack, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { useAuth } from "@/features/authentication";
import { formatMoney, formatNumber } from "@/features/shared";
import { ExpenseDialog } from "@/features/ui";

import { EXPENSE_ACCOUNT_TYPES } from "../expense-accrual-form-constants";

export const ExpenseAccrualDetailsSection = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { outlet } = useAuth();

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "expenseAccrualDetails",
  });

  const expenseAccrualDetails = useWatch({
    control,
    name: "expenseAccrualDetails",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const total = useMemo(
    () =>
      (expenseAccrualDetails || []).reduce(
        (sum, line) => sum + (Number(line.amount) || 0),
        0
      ),
    [expenseAccrualDetails]
  );

  const handleAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleSave = (data) => {
    if (editingIndex !== null) {
      update(editingIndex, { ...fields[editingIndex], ...data });
    } else {
      append(data);
    }
  };

  const columns = [
    {
      field: "outletChartOfAccount",
      headerName: "Akun",
      flex: 3,
      sortable: false,
      valueGetter: (account) =>
        account ? `${account.code} - ${account.name}` : "-",
    },
    {
      field: "amount",
      headerName: "Nilai (Rp)",
      flex: 1.5,
      type: "number",
      sortable: false,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 2,
      sortable: false,
      valueGetter: (note) => note || "-",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Aksi",
      getActions: (params) => {
        const index = fields.findIndex((field) => field.id === params.id);
        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Ubah"
            onClick={() => handleEdit(index)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Hapus"
            onClick={() => remove(index)}
            color="error"
          />,
        ];
      },
    },
  ];

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6">Rincian Beban</Typography>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={handleAdd}
            disabled={!outlet?.id}
          >
            Tambah Baris
          </Button>
        </Stack>

        <DataGrid
          rows={fields}
          columns={columns}
          getRowId={(row) => row.id}
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          hideFooter
        />

        {errors.expenseAccrualDetails?.message && (
          <Typography variant="body2" color="error">
            {errors.expenseAccrualDetails.message}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={2}
          sx={{ justifyContent: "flex-end", alignItems: "baseline" }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Total
          </Typography>
          <Typography variant="h6">{formatMoney(total)}</Typography>
        </Stack>

        {dialogOpen && (
          <ExpenseDialog
            outletId={outlet?.id}
            title={editingIndex !== null ? "Ubah Baris" : "Tambah Baris"}
            amountLabel="Nilai"
            allowedAccountTypes={EXPENSE_ACCOUNT_TYPES}
            requirePositiveAmount
            onClose={() => setDialogOpen(false)}
            onSubmit={handleSave}
            defaultValues={
              editingIndex !== null ? fields[editingIndex] : null
            }
          />
        )}
      </Stack>
    </Paper>
  );
};
